import { MythicPlusTopPlayers } from './mythicPlusTopPlayers';
import { t } from 'i18next';
import { Col } from '../../../../../../../shared/frontend/components/grid/Col';
import { Row } from '../../../../../../../shared/frontend/components/grid/Row';
import KeystoneRunRankingsContent from '../keystoneRunRankingsContent';
import { createTopMPlusRunsParams, getTopMPlusInstanceId } from './mythicPlusTopRunsPane/utils';
import { KeystoneRunRankingsTableFrontpage } from '../../presentational/keystoneRunRankingsElements/KeystoneRunRankingsTableFrontpage';
import { getSeasonBySlug } from '../../../../../shared/enums';
import _ from 'lodash';
import { TAction } from '../../../../../../../shared/frontend/scripts/actions/types/action';
import { apiServiceAsync } from '../../../services/apiService';
import KeystoneActionCreator from '../../../actions/keystoneActionCreator';
import { asset } from '../../../../../../../shared/frontend/services/assetResolverService';

export type TRemixSectionProps = {
  enabled: boolean;
  seasonSlug: string;
  region: string;
  realm?: string;
  soloSeasonSlug?: string;
};

export function RemixSection(props: TRemixSectionProps) {
  if (!props.enabled) {
    return null;
  }

  const season = getSeasonBySlug(props.seasonSlug);
  if (!season) {
    return null;
  }

  const soloSeason = props.soloSeasonSlug ? getSeasonBySlug(props.soloSeasonSlug) : null;

  const topMplusSoloProps = {
    ...props,
    seasonSlug: props.soloSeasonSlug!
  };

  const seasonLogo = <img
    className="legion-remix-icon"
    src={asset('/images/legion_remix.png')}
    alt="Legion Remix Logo"
    style={{
      position: 'relative',
      top: '-6px',
      marginRight: '0.25rem',
      marginBottom: '-10px',
      left: '10px',
    }}
  />;

  return (
    <>
      <MythicPlusTopPlayers {...props} seasonLogo={seasonLogo} />
      {props.soloSeasonSlug && <MythicPlusTopPlayers {...props} seasonSlug={props.soloSeasonSlug} seasonLogo={seasonLogo} />}

      <div className="slds-m-bottom--small slds-m-top--large">
        <h2>
          <span className="rio-text-title--caps text-raiderio">{t('frontpage:section-header-top-mplus-runs')}</span>
          {seasonLogo}
          <span className="slds-m-left--small slds-text-title--caps text-white">
            {t(`seasons:${season.slug}.name`)} {season.affix && <>({t(`seasons:${season.slug}.affix`)})</>}
          </span>
        </h2>
      </div>

      <Row gutter={['xx-small', 'xx-small']}>
        <Col size={12} md={6} grow className="slds-is-relative">
          <KeystoneRunRankingsContent
            instanceId={getTopMPlusInstanceId(props)}
            title={t(`seasons:${season.slug}.name`)}
            type="leaderboards-strict"
            component={KeystoneRunRankingsTableFrontpage}
            {...createTopMPlusRunsParams(props)}
            columns={{
              rank: true,
              time: false,
              tank: true,
              healer: true,
              dps: true,
              faction: false,
              dungeon: true,
              roleIcons: false,
              video: true,
              level: true
            }}
          />
        </Col>
        {props.soloSeasonSlug && soloSeason && (
          <Col size={12} md={6} grow className="slds-is-relative">
            <KeystoneRunRankingsContent
              instanceId={getTopMPlusInstanceId(topMplusSoloProps)}
              title={t(`seasons:${soloSeason.slug}.name`)}
              type="leaderboards-strict"
              component={KeystoneRunRankingsTableFrontpage}
              {...createTopMPlusRunsParams(topMplusSoloProps)}
              columns={{
                rank: true,
                time: false,
                tank: true,
                healer: true,
                dps: true,
                faction: false,
                dungeon: true,
                roleIcons: false,
                video: true,
                level: true
              }}
            />
          </Col>
        )}
      </Row>
    </>
  );
}

(RemixSection as any).preloadComponentActions = async (props: TRemixSectionProps, params: any): Promise<TAction<any>[]> => {
  if (!props.enabled) {
    return [];
  }

  const getRankingsActions = async (seasonSlug: string): Promise<TAction<any>> => {
    const loadParams = createTopMPlusRunsParams({ ...props, seasonSlug });
    const data = await apiServiceAsync.getKeystoneRunRankings(loadParams);
    return KeystoneActionCreator.setKeystoneRankings(getTopMPlusInstanceId({ ...props, seasonSlug }), data.rankings);
  };

  return _.flatten(
    await Promise.all([
      (MythicPlusTopPlayers as any).preloadComponentActions(props),
      ...(props.soloSeasonSlug
        ? [
            (MythicPlusTopPlayers as any).preloadComponentActions({
              ...props,
              seasonSlug: props.soloSeasonSlug
            })
          ]
        : []),
      getRankingsActions(props.seasonSlug),
      ...(props.soloSeasonSlug ? [getRankingsActions(props.soloSeasonSlug)] : [])
    ])
  );
};
